import { useEffect, useState, useRef, useCallback } from 'react'
import styles from './restosList.module.scss'
import { MarkerData } from 'Types/markers'
import styled, { useTheme } from 'styled-components'
import { device } from '../breakpoints'
import { Button } from '@mui/material'
import locales from 'locales/locales'

declare global {
  interface Window {
    dataLayer: any
  }
}

function getDistanceFromLatLonInKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  var R = 6371 // Radius of the earth in km
  var dLat = deg2rad(lat2 - lat1) // deg2rad below
  var dLon = deg2rad(lon2 - lon1)
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
    Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2)
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  var d = R * c // Distance in km
  return d
}

function deg2rad(deg: number) {
  return (deg * Math.PI) / 180
}

// No "Cuisine" for the German version
function getCuisine(filters: any, showCuisine: boolean) {
  const cuisine = filters.find((e: any) => e.name === 'Cuisine').value
  if (cuisine.includes('cuisine')) {
    return cuisine.replace('cuisine', 'Cuisine')
  } else {
    return <>{showCuisine ? 'Cuisine ' : ''} {cuisine}</>
  }
}

function getDistanceStr(meters: number) {
  if (meters < 1000) {
    return (Math.round(meters / 10) * 10).toString() + 'm'
  } else if (meters < 4000) {
    return (Math.round(meters / 100) / 10).toString() + 'km'
  } else {
    return Math.round(meters / 1000).toString() + 'km'
  }
}

export const RestosList = ({
  restos,
  handleClick,
  userLocation,
}: {
  restos: MarkerData[]
  handleClick: (marker: MarkerData) => void
  userLocation: { lat: number; lng: number } | null | undefined
}) => {
  const countStep = 12
  const [showCount, setShowCount] = useState(countStep)
  const [sortedRestos, setSortedRestos] = useState<MarkerData[]>([])
  const loader = useRef(null)
  const theme = useTheme()

  // If there is a new list of restos or a new user location, add the distance to the user and sort them
  useEffect(() => {
    if (userLocation) {
      const list = restos.map((resto) => ({
        ...resto,
        distance: getDistanceFromLatLonInKm(
          userLocation.lat,
          userLocation.lng,
          resto.latitude,
          resto.longitude
        ),
      }))

      setSortedRestos(list.sort((a, b) => a.distance - b.distance))
    } else {
      setSortedRestos(restos)
    }
    // setSortedRestos(
    // .sort((a, b) => a.ts - b.ts)
    // .sort((a, b) => {
    //   if (a.longitude >= 6.37557) return -1
    //   // if (publisherDomain === 'tdg.ch') {
    //   //   if (a.longitude < 6.37557) return -1
    //   // } else {
    //   //   if (a.longitude >= 6.37557) return -1
    //   // }
    // })
    // )
  }, [restos, userLocation])

  // const handleObserver = useCallback((entries:any) => {
  //   console.log(showCount)
  //   const target = entries[0]
  //   if (target.isIntersecting) {
  //     console.log('visible')
  //     setShowCount((count) => count + countStep)
  //   }
  // }, [])

  // useEffect(() => {
  //   const option = {
  //     root: null,
  //     rootMargin: "0px",
  //     threshold: 0
  //   }
  //   const observer = new IntersectionObserver(handleObserver, option)
  //   if (loader.current) observer.observe(loader.current)
  // }, [handleObserver])

  return (
    <Container>
      <ListContent>
        {sortedRestos.slice(0, showCount).map((resto) => (
          <ItemContainer
            key={`resto-${resto.markerId}`}
            onClick={() => {
              handleClick(resto)

              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: `showresto:${resto.markerId}`,
              })
            }}
          >
            <ImageContainer>
              <div
                style={{
                  backgroundImage: `url(${resto.image})`,
                }}
                className={styles.image}
              />
            </ImageContainer>

            <ItemContent>
              <div>
                <ItemTitle>{resto.name}</ItemTitle>
                <ItemDescription>{getCuisine(resto.filters, theme.lang !== 'fr' ? false : true)}</ItemDescription>
                {resto?.distance && (
                  <ItemDistance>
                    {getDistanceStr(resto?.distance * 1000)}
                  </ItemDistance>
                )}
              </div>
              {['bernerzeitung', 'derbund'].includes(theme.publisherDomain) ?
                <RegionContainer>{resto?.typeShort}</RegionContainer> :
                <CoinsContainer>
                  <CoinsBox>
                    {new Array(resto?.filters.find((e) => e.name === 'Prix')?.value)
                      .fill(0)
                      .map((coin, i) => (
                        <span key={`coin-${i}`}>$</span>
                      ))}
                  </CoinsBox>
                </CoinsContainer>
              }
            </ItemContent>
          </ItemContainer>
        ))}
      </ListContent>
      <div
        ref={loader}
        style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}
      >
        {sortedRestos.length > showCount && (
          <Button
            variant='contained'
            disableElevation
            onClick={() => {
              setShowCount(showCount + countStep)

              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: `showmore`,
              })
            }}
          >
            {locales.buttonSeeMore[theme.lang]}
          </Button>
        )}
      </div>
    </Container>
  )
}

const CoinsContainer = styled.div`
  text-align: right;
`

const CoinsBox = styled.div`
  background: ${(props) => props.theme.greyColor};
  color: ${(props) => props.theme.textColor};
  display: inline-block;
  font-size: 0.8em;
  padding: 2px 8px;
  border-radius: 40px;
`

const ItemContent = styled.div`
  width: 100%;
  background: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  overflow: hidden;
  display: grid;
  grid-template-columns: 2fr 1fr;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding-top: 0.65em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding-top: 0.65em;
  }
`

const ItemTitle = styled.div`
  font-family: var(--font-sans-serif);
  font-weight: 600;
  font-size: 1em;
  line-height: 1.2em;
  margin-bottom: 0.15em;
`

const ItemDistance = styled.div`
  font-size: 0.8em;
  color: #a2a3a5;
`

const ItemDescription = styled.div`
  font-family: var(--font-sans-serif);
  overflow: hidden;
  width: 100%;
  position: relative;
  font-size: 0.865em;
  line-height: 1.2em;
  // white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  opacity: 0.55;
`

const RegionContainer = styled.div`
  text-align: right;
  font-size: 0.865em;
  line-height: 1.2em;
  opacity: 0.55;
`

const Container = styled.div`
  position: relative;
`

const ListContent = styled.div`
  position: relative;
  display: grid;
  padding: 1em;
  padding-top: 0;
  grid-auto-rows: minmax(min-content, max-content);

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-gap: 30px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 20px;
  }

  @media ${device.laptopL} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 20px;
  }
`

const ItemContainer = styled.div`
  display: grid;
  position: relative;
  cursor: pointer;
  width: 100%;
  overflow: hidden;

  &:last-child {
    border: none;
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(min-content, max-content) 1fr;
  }
`

const ImageContainer = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${(props) => props.theme.mainColor};
  background-size: cover;
  background-position: left center;
`
